import { toastr } from "react-redux-toastr";
import i18n from "i18n";
import {
	UNDO_CLEAR_SCOPE,
	UNDO_MARK_UNDONE,
	UNDO_REGISTER_COMMAND,
	UNDO_REMOVE_COMMAND,
} from "utils/undo/constants";
import { createUndoId } from "utils/undo/createUndoId";
import { executeInverseRequests } from "utils/undo/executeInverseRequests";
import { isUndoToastsEnabled } from "utils/undo/isUndoToastsEnabled";
import { runUndoRefresh } from "utils/undo/undoRefreshRegistry";
import {
	selectActiveUndoCommand,
	selectUndoScope,
	selectVisibleUndoCommands,
} from "reducers/undoHistoryReducer";
import {
	refreshScopeSuccessToasts,
	showSuccessToast,
	showUndoableSuccessToast,
} from "utils/toast/showUndoableSuccessToast";

function createOnUndo(dispatch, scope, command) {
	return () => dispatch(executeUndo(scope, command.id));
}

function refreshStack(dispatch, getState, scope) {
	refreshScopeSuccessToasts({
		dispatch,
		getState,
		scope,
		selectActiveUndoCommand,
		selectVisibleUndoCommands,
		createOnUndo: (command) => createOnUndo(dispatch, scope, command),
	});
}

export const clearUndoScope = (scope) => ({
	type: UNDO_CLEAR_SCOPE,
	payload: { scope },
});

export const registerUndoCommand =
	({
		scope,
		type,
		entity,
		messageKey,
		messageParams = {},
		inverse,
		refresh,
	}) =>
	(dispatch, getState) => {
		if (!isUndoToastsEnabled() || !inverse?.requests?.length) {
			return null;
		}

		const command = {
			id: createUndoId("cmd"),
			type,
			entity,
			messageKey,
			messageParams,
			toastId: null,
			toastVisible: true,
			createdAt: Date.now(),
			undone: false,
			inverse,
			refresh: refresh || {},
		};

		dispatch({
			type: UNDO_REGISTER_COMMAND,
			payload: { scope, command },
		});

		refreshStack(dispatch, getState, scope);

		return selectUndoScope(getState(), scope).commands.find(
			(item) => item.id === command.id
		);
	};

export const registerUndoAndShowToast = (payload) => (dispatch) => {
	if (!isUndoToastsEnabled() || !payload.inverse?.requests?.length) {
		showSuccessToast({
			message: i18n.t(payload.messageKey, payload.messageParams),
		});
		return null;
	}

	return dispatch(registerUndoCommand(payload));
};

export const executeUndo = (scope, commandId) => async (dispatch, getState) => {
	if (!isUndoToastsEnabled()) {
		return false;
	}

	const scopeState = selectUndoScope(getState(), scope);
	const command = scopeState.commands.find((item) => item.id === commandId);

	if (!command || command.undone) {
		return false;
	}

	const response = await executeInverseRequests(command.inverse.requests).catch(
		(error) => ({
			ok: false,
			errors: [{ description: error?.message || i18n.t("undo.error") }],
		})
	);

	if (response?.ok === false) {
		const isConflict =
			response.status === 409 ||
			/conflict|409|obsoleto|modificad/i.test(
				response.errors?.[0]?.description || ""
			);

		if (isConflict) {
			dispatch(removeUndoCommand(scope, commandId));
		}

		runUndoRefresh(command);

		toastr.error(
			i18n.t("common.error"),
			response.errors?.[0]?.description || i18n.t("undo.error")
		);
		return false;
	}

	dispatch({
		type: UNDO_MARK_UNDONE,
		payload: { scope, commandId },
	});

	if (command.toastId) {
		toastr.remove(command.toastId);
	}

	runUndoRefresh(command);

	showSuccessToast({ message: i18n.t("undo.undone") });

	const activeCommand = selectActiveUndoCommand(getState(), scope);
	if (activeCommand) {
		showUndoableSuccessToast({
			dispatch,
			scope,
			command: activeCommand,
			isActive: true,
			onUndo: createOnUndo(dispatch, scope, activeCommand),
		});
	}

	return true;
};

export const executeActiveUndo = (scope) => (dispatch, getState) => {
	const activeCommand = selectActiveUndoCommand(getState(), scope);
	if (!activeCommand) {
		return false;
	}

	return dispatch(executeUndo(scope, activeCommand.id));
};

export const removeUndoCommand = (scope, commandId) => ({
	type: UNDO_REMOVE_COMMAND,
	payload: { scope, commandId },
});

export const refreshVisibleStackToasts = (dispatch, getState, scope) => {
	refreshStack(dispatch, getState, scope);
};
